import { Component, OnInit , Injectable}  from '@angular/core';
import { Router, ActivatedRoute }     from '@angular/router';
import { filter }                     from 'rxjs/operators';

import { OauthService }      from '../../auth/oauth2-service';
import { AuthService }       from '../../auth/auth.service';
import { MsalAccessService } from '../../auth/msal-service';
import { Microsoft }         from '../../auth/microsoft.service';
import { getIdp, getRedirect, clearIdp, clearRedirect } from '../../shared/oidc-store';

@Component({
  selector   : 'oidc-callback',
  templateUrl: './oidc-callback.component.html',
  standalone : false
})
export class OidcCallbackComponent implements OnInit {

  private fromChat = false;

  // Simple cookie reader to pull out Django's CSRF token
  private getCookie(name: string): string {
    const match = document.cookie.match(
      new RegExp('(^|; )' +
        name.replace(/([.*+?^${}()|\[\]\/\\])/g, '\\$1') +
      '=([^;]*)')
    );
    return match ? decodeURIComponent(match[2]) : '';
  }

  constructor(
    private router       : Router,
    private oauthService : OauthService,
    private authService  : AuthService,
    private msalInstance : MsalAccessService,
    private msauthService: Microsoft,
    private route        : ActivatedRoute
  ) {}

  //──────────────────────────────────────────────────────────────
  ngOnInit(): void {

    /* — where did we come from? — */
    this.fromChat = localStorage.getItem('fromComponent') === 'chat';
    localStorage.removeItem('fromComponent');

    /* — which IdP started the flow? — */
    const idp  = getIdp();
    const code = this.route.snapshot.queryParamMap.get('code');
    // Bail early if getIdp() fails for any reason
    if (!idp || !code) {
      console.error('OIDC callback missing idp or code');
      this.router.navigate(['/login'], {
        queryParams: { oidc_error: 'OIDC authentication failed' }
      });
      return;
    }

    /* ───────────────────────── GOOGLE GSI ───────────────────── */
    if (code && idp === 'Google'){
      const redirectUri = getRedirect();
    
      clearIdp();
      clearRedirect();

      fetch('/api/swirl/oidc_authenticate/?idp=Google', {
        method      : 'POST',
        credentials : 'include',
        headers     : {
                    'Content-Type':  'application/json',
                    'X-CSRFToken':   this.getCookie('csrftoken')
                  },
        body        : JSON.stringify({ code, redirect_uri: redirectUri })
      })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => this.handleBackendLogin(d))
      .catch(() => this.router.navigate(['/login'], {
        queryParams: { oidc_error: 'OIDC authentication failed' }
      }));
      return;
    }

    /* ─────────────── PKCE-based OIDC (angular-oauth2-oidc) ─────────────── */
    this.oauthService.events
        .pipe(filter((e: any) => e.type === 'token_received'))
        .subscribe(() => {
          const token = this.oauthService.getAccessToken();
          if (!token) { return; }

          this.authService.oidcLogin(token, idp).subscribe(
            (data: any)  => this.handleBackendLogin(data),
            (error: any) => console.error(error)
          );
        });
  }

  //──────────────────────────────────────────────────────────────
  private handleBackendLogin(data: any): void {

    if (!data?.token){
      this.router.navigate(['/login'], {
        queryParams: { oidc_error: 'OIDC authentication failed' }
      });
      return;
    }

    this.authService.setToken(data.token);
    this.authService.setUser (data.user);

    if (data.ai_provider_exists){
      this.authService.setAIProviderStatus(
          data.ai_provider_exists.toLowerCase() === 'true');
    }

    if (data.chat_view)
      this.authService.setChatStatus(data.chat_view.toLowerCase() === 'true');

    if (data.prompts_exists)
      this.authService.setPromptsStatus(data.prompts_exists.toLowerCase() === 'true');

    if (data.rag_rating_enabled)
      this.authService.setRagRatingStatus(data.rag_rating_enabled.toLowerCase() === 'true');

    if (data.bulk_oauth2_enabled)
      this.authService.setBulkOAuth2Status(data.bulk_oauth2_enabled.toLowerCase() === 'true');

    if (data.confidence_mixer)
      this.authService.setConfidenceMixerStatus(data.confidence_mixer.toLowerCase() === 'true');

    /* Propagate MS access-token when the OIDC flow came from MSAL */
    const oauthTok = this.oauthService.getAccessToken();
    if (oauthTok){
      this.msalInstance.setIsTokenFromOauth(true);
      this.msauthService.setToken(oauthTok);
    }

    /* where to land? */
    const dest = (this.fromChat && this.authService.getChatStatus())
                 ? '/chat' : '/';
    this.router.navigate([dest]);
  }
}
